import { useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useAuthStore } from '@/stores/auth.store';
import { OWNER_TENANT_SLUG } from '@/lib/owner';
import { type ColumnDef } from '@tanstack/react-table';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Copy, Ban, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { TopBanner } from '@/components/TopBanner';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useInvitations, useCreateInvitation, useRevokeInvitation } from '@/hooks/useInvitations';
import type { Invitation } from '@/api/invitations.api';

export const Route = createFileRoute('/_authenticated/invitaciones')({
  // Solo el dueno de la plataforma: los clientes (master de su tenant) no pasan.
  beforeLoad: () => {
    if (useAuthStore.getState().tenantSlug !== OWNER_TENANT_SLUG) {
      throw redirect({ to: '/chats' });
    }
  },
  component: InvitacionesPage,
});

const createSchema = z.object({
  platform: z.enum(['ganamos', 'casinozeus', 'kingbox', 'soyjoker']),
  slug: z.string().min(1, 'Ingresá un slug'),
  displayName: z.string().min(1, 'Ingresá el nombre'),
  hgAccountId: z.string().optional(),
});
type CreateForm = z.infer<typeof createSchema>;

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  pending: 'default',
  consumed: 'secondary',
  revoked: 'destructive',
  expired: 'outline',
};

function InvitacionesPage() {
  const invitations = useInvitations();
  const revoke = useRevokeInvitation();
  const [dialogOpen, setDialogOpen] = useState(false);

  const copyLink = (inv: Invitation) => {
    navigator.clipboard.writeText(inv.url).then(
      () => toast.success('Link copiado'),
      () => toast.error('No se pudo copiar'),
    );
  };

  const columns: ColumnDef<Invitation>[] = [
    { accessorKey: 'displayName', header: 'Nombre' },
    { accessorKey: 'slug', header: 'Slug' },
    { accessorKey: 'platform', header: 'Plataforma' },
    {
      accessorKey: 'status',
      header: 'Estado',
      cell: ({ row }) => {
        const s = row.original.status;
        return <Badge variant={STATUS_VARIANT[s] ?? 'outline'}>{s}</Badge>;
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Creada',
      cell: ({ row }) => new Date(row.original.createdAt).toLocaleDateString('es-AR'),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const inv = row.original;
        if (inv.status !== 'pending') return null;
        return (
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => copyLink(inv)}>
              <Copy className="mr-1 h-3.5 w-3.5" /> Link
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => revoke.mutate(inv.id)}
              disabled={revoke.isPending}
            >
              <Ban className="mr-1 h-3.5 w-3.5" /> Revocar
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex h-full flex-col">
      <TopBanner title="Invitaciones" subtitle="Alta self-service de clientes" />
      <div className="flex-1 space-y-4 p-6">
        <div className="flex justify-end">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" /> Nueva invitación
              </Button>
            </DialogTrigger>
            <CreateDialog onDone={() => setDialogOpen(false)} />
          </Dialog>
        </div>

        <DataTable
          columns={columns}
          data={invitations.data ?? []}
          isLoading={invitations.isLoading}
          emptyMessage="No hay invitaciones todavía"
        />
      </div>
    </div>
  );
}

function CreateDialog({ onDone }: { onDone: () => void }) {
  const create = useCreateInvitation();
  const form = useForm<CreateForm>({
    resolver: zodResolver(createSchema),
    defaultValues: { platform: 'ganamos', slug: '', displayName: '', hgAccountId: '' },
  });

  const onSubmit = (values: CreateForm) => {
    create.mutate(
      {
        platform: values.platform,
        slug: values.slug.trim(),
        displayName: values.displayName.trim(),
        hgAccountId: values.hgAccountId?.trim() || null,
      },
      {
        onSuccess: (inv) => {
          navigator.clipboard.writeText(inv.url).catch(() => undefined);
          toast.success('Invitación creada — link copiado al portapapeles');
          form.reset();
          onDone();
        },
      },
    );
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Nueva invitación</DialogTitle>
        <DialogDescription>
          Generá un link de alta para un cliente nuevo. Se copia al portapapeles al crearlo.
        </DialogDescription>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="platform"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Plataforma</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Elegí plataforma" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="ganamos">Ganamos</SelectItem>
                    <SelectItem value="casinozeus">CasinoZeus</SelectItem>
                    <SelectItem value="kingbox">Kingbox</SelectItem>
                    <SelectItem value="soyjoker">Soyjoker</SelectItem>
                    <SelectItem value="soyjoker">Soyjoker</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="slug"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Slug</FormLabel>
                <FormControl>
                  <Input placeholder="cliente-nuevo" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="displayName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nombre visible</FormLabel>
                <FormControl>
                  <Input placeholder="Cliente Nuevo" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="hgAccountId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>HG Account ID (opcional)</FormLabel>
                <FormControl>
                  <Input placeholder="uuid del accountId de HG Cash" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <DialogFooter>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Crear y copiar link
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </DialogContent>
  );
}
